import { ComponentProps, FC, ReactNode } from "react";
import { useFormContext } from "react-hook-form";
import { useHookFormMask } from "use-mask-input";

import { I18nKeyType } from "@libs/i18n";
import { useTranslate } from "@/hooks/useTranslate";
import { cn } from "@/utils/tailwind";

export type InputMaskType = {
  fieldName: string;
  mask: string[];
  required?: boolean;
};

export type BaseTextInputPropsType = ComponentProps<"input"> & {
  i18nKey?: I18nKeyType;
  i18nOptions?: object;
  leftElement?: ReactNode | string;
  rightElement?: ReactNode | string;
  mask?: InputMaskType;
};

export const BaseTextInput: FC<BaseTextInputPropsType> = ({
  ref,
  className,
  type,
  mask,
  i18nKey,
  i18nOptions,
  leftElement,
  rightElement,
  value,
  onChange,
  ...props
}) => {
  const { register } = useFormContext();
  const registerWithMask = useHookFormMask(register);

  const placeholder = useTranslate(i18nKey, i18nOptions);
  const hasLeftElement = Boolean(leftElement);
  const hasRightElement = Boolean(rightElement);

  return (
    <div className="relative inline-flex flex-1 items-center">
      {hasLeftElement && (
        <div className="absolute top-0 bottom-0 left-2 flex w-4 items-center justify-center text-muted-foreground">
          {leftElement}
        </div>
      )}
      <input
        type={type}
        data-slot="input"
        placeholder={placeholder}
        {...(mask
          ? registerWithMask(mask.fieldName, mask.mask, {
              autoUnmask: true,
              required: mask.required,
            })
          : { ref })}
        className={cn(
          "flex h-9 w-full min-w-0 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-xs transition-[color,box-shadow] outline-none selection:bg-primary selection:text-primary-foreground file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30",
          "focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50",
          "aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40",
          hasLeftElement ? "pl-7.5" : "",
          hasRightElement ? "pr-7.5" : "",
          className,
        )}
        {...props}
        value={value ?? ""}
        onChange={onChange ?? undefined}
        readOnly={onChange ? false : true}
        defaultValue={undefined}
      />
      {hasRightElement && (
        <div className="absolute top-0 right-2 bottom-0 flex w-4 items-center justify-center text-muted-foreground">
          {rightElement}
        </div>
      )}
    </div>
  );
};
